import standardEquipmentCatalogueJson from "./components/CoilConfigurator/standard-equipment-catalogue.json";
import recommendationAssumptionsJson from "./components/CoilConfigurator/recommendation-assumptions.json";

export type RecommendationStatus =
  | "recommended"
  | "marginal"
  | "not supported";

export interface FieldJointGeometryInput {
  pipeOutsideDiameterMm: number;
  pipeWallThicknessMm: number;
  factoryCoatingThicknessMm: number;
  sideACutbackMm: number;
  sideBCutbackMm: number;
}

export interface HeatingInput {
  ambientTemperatureC?: number;
  startingTemperatureC?: number;
  targetHeatTemperatureC: number;
}

export interface ApplicationProductInput {
  id: string;
  name: string;
  subsequentProcessTemperatureC?: number;
}

export interface PowerSource {
  id: string;
  name: string;
  powerSourceRatingKva: number;
  powerSourceFrequencyHz: number;
  maximumPowerSourceVoltageV: number;
  maximumPowerSourceCurrentA: number;
  operatingLimits: PowerSourceOperatingLimits;
}

export interface PowerSourceOperatingLimits {
  minimumPowerUtilization: number;
  maximumPowerUtilization: number;
}

export interface CoilConfiguration {
  id: string;
  name: string;
  coilFrameId?: string;
  bottomEndId?: string;
  coilFrameSizeInches?: number;
  coilFrameInsideDiameterMm?: number;
  coilFrameSizeMm: number;
  numberOfTurns: number;
  turnSpacingMm: number;
  coilCoverageMm: number;
  heatingResponseCelsiusMeterPerKwMinute: number;
}

export interface CoilFrame {
  id: string;
  name: string;
  coilFrameSizeInches: number;
  coilFrameInsideDiameterMm: number;
}

export interface BottomEnd {
  id: string;
  name: string;
  numberOfTurns: number;
  firstContactEdgeOffsetMm: number;
  lastContactEdgeOffsetMm: number;
  contactCenterSpacingsMm: number[];
  bottomEndWidthMm: number;
  heatingResponseCelsiusMeterPerKwMinute: number;
  coilSectionMm?: number;
  averageContactCenterSpacingMm?: number;
  generatedTurnSpacingMm?: number;
}

export interface StandardEquipmentCatalogue {
  powerSources: PowerSource[];
  coilFrames?: CoilFrame[];
  bottomEnds?: BottomEnd[];
  coilConfigurations?: CoilConfiguration[];
}

interface NormalizedStandardEquipmentCatalogue {
  powerSources: PowerSource[];
  coilFrames: CoilFrame[];
  bottomEnds: NormalizedBottomEnd[];
  coilConfigurations: CatalogueMatchedCoilConfiguration[];
}

interface CatalogueMatchedCoilConfiguration extends CoilConfiguration {
  effectiveCoilInsideDiameterMm: number;
  heatingLengthMm: number;
  selectedCoilFrame?: CoilFrame;
  selectedBottomEnd?: NormalizedBottomEnd;
}

interface NormalizedBottomEnd extends BottomEnd {
  coilSectionMm: number;
  averageContactCenterSpacingMm: number;
  generatedTurnSpacingMm: number;
}

export interface RecommendSetupInput {
  fieldJointGeometry: FieldJointGeometryInput;
  heating: HeatingInput;
  applicationProduct?: ApplicationProductInput;
  selectedPowerSourceId?: string;
  availablePowerSourceIds?: string[];
  standardEquipmentCatalogue?: StandardEquipmentCatalogue;
  coilClearanceMm?: number;
  requiredOverhangMm?: number;
  marginalRecommendationRules?: MarginalRecommendationRules;
  recommendationOptions?: RecommendationOptionsInput;
}

export interface SelectedCoilConfiguration {
  id: string;
  name: string;
  coilFrameId?: string;
  bottomEndId?: string;
  coilFrame?: CoilFrame;
  bottomEnd?: BottomEnd;
  coilFrameSizeInches?: number;
  coilFrameInsideDiameterMm?: number;
  effectiveCoilInsideDiameterMm?: number;
  coilFrameSizeMm: number;
  numberOfTurns: number;
  turnSpacingMm: number;
  coilCoverageMm: number;
  coilSectionMm?: number;
  heatingLengthMm?: number;
}

export interface RecommendationCalculations {
  factoryCoatingOutsideDiameterMm: number;
  bareSteelLengthMm: number;
  requiredCoilInsideDiameterMm: number;
  requiredCoilCoverageMm: number;
  heatingLengthMm: number | null;
}

export type PowerSourceOutputLimitReason = "voltage" | "current";

export interface OperatingElectricalValues {
  powerSourceVoltageV: number;
  powerSourceCurrentA: number;
  powerFactor: number;
  outputLimited: boolean;
  outputLimitingReasons: PowerSourceOutputLimitReason[];
}

export type RecommendationAdvisoryCauseCode =
  | "coil-frame-size-below-required"
  | "coil-frame-size-close-to-required"
  | "coil-coverage-below-required"
  | "coil-coverage-close-to-required"
  | "low-power-utilization"
  | "output-limited";

export interface RecommendationAdvisoryCause {
  code: RecommendationAdvisoryCauseCode;
  message: string;
  assumptionName?: string;
  measuredValue?: number;
  thresholdValue?: number;
  deficitValue?: number;
  outputLimitingReasons?: PowerSourceOutputLimitReason[];
}

export type RecommendationAdvisoryActionCode =
  | "engineering-review"
  | "custom-coil-design-review"
  | "review-output-limiting";

export interface RecommendationAdvisoryAction {
  code: RecommendationAdvisoryActionCode;
  message: string;
  causeCodes: RecommendationAdvisoryCauseCode[];
}

interface OperatingPoint {
  operatingElectricalValues: OperatingElectricalValues;
  deliveredPowerKw: number;
  powerUtilization: number;
  estimatedPowerUtilization: number;
}

export interface OptimumCoilRecommendation {
  numberOfTurns: number;
  turnSpacingMm: number;
  coilCoverageMm: number;
}

export interface CatalogueMatchedCoilComparison {
  numberOfTurns: number;
  turnSpacingMm: number;
  coilCoverageMm: number;
  coilSectionMm: number | null;
  heatingLengthMm: number | null;
}

export interface CoilRecommendationComparison {
  optimum: OptimumCoilRecommendation;
  catalogueMatch: CatalogueMatchedCoilComparison | null;
}

export interface CatalogueEvaluation {
  standardCoilConfigurationCount: number;
  fittingCoilConfigurationCount: number;
  hasCoilFrameSizeFit: boolean;
  hasCoilCoverageFit: boolean;
}

export interface MarginalRecommendationRules {
  minimumCoilFrameSizeMarginMm: number;
  minimumCoilCoverageMarginMm: number;
  powerUtilizationBoundaryMargin: number;
}

export interface TurnBounds {
  minimumTurns: number;
  maximumTurns: number;
}

export interface RecommendationModelCoefficients {
  powerUtilizationBase: number;
  powerUtilizationFrequencyCoefficient: number;
  powerUtilizationVoltageCoefficient: number;
  powerUtilizationCurrentCoefficient: number;
  powerUtilizationPipeOutsideDiameterCoefficient: number;
  powerUtilizationPipeWallThicknessCoefficient: number;
  powerUtilizationFrameDiameterCoefficient: number;
  powerUtilizationTurnsCoefficient: number;
  powerUtilizationBottomEndWidthCoefficient: number;
  powerUtilizationTurnSpacingCoefficient: number;
  heatingCycleFrequencyCoefficient: number;
  cooldownFrequencyCoefficient: number;
  cooldownHeatingCycleHeatSoakCoefficient: number;
  cooldownPipeWallThicknessCoefficient: number;
}

export interface FrequencyCalibrationPoint {
  frequencyHz: number;
  heatingCycleFactor: number;
  cooldownFactor: number;
}

export interface AdvisoryThresholds {
  lowPowerUtilization: number;
  minimumCoilFrameSizeMarginMm: number;
  minimumCoilCoverageMarginMm: number;
  powerUtilizationBoundaryMargin: number;
}

export interface AdvancedRecommendationCalibration {
  modelCoefficients: RecommendationModelCoefficients;
}

export interface RecommendationOptions {
  referenceFrequencyHz: number;
  preferredTurnSpacingMm: number;
  turnBounds: TurnBounds;
  coilClearanceMm: number;
  requiredOverhangMm: number;
  tieBarDiameterMm: number;
  frequencyCalibrationCurve: FrequencyCalibrationPoint[];
  advisoryThresholds: AdvisoryThresholds;
  advancedCalibration: AdvancedRecommendationCalibration;
  modelCoefficients: RecommendationModelCoefficients;
}

export interface RecommendationOptionsInput {
  referenceFrequencyHz?: number;
  preferredTurnSpacingMm?: number;
  turnBounds?: Partial<TurnBounds>;
  coilClearanceMm?: number;
  requiredOverhangMm?: number;
  tieBarDiameterMm?: number;
  frequencyCalibrationCurve?: FrequencyCalibrationPoint[];
  advisoryThresholds?: Partial<AdvisoryThresholds>;
  advancedCalibration?: {
    modelCoefficients?: Partial<RecommendationModelCoefficients>;
  };
  modelCoefficients?: Partial<RecommendationModelCoefficients>;
}

export type StartingTemperatureSource =
  | "starting temperature input"
  | "ambient temperature input"
  | "default ambient temperature";

export interface HeatingCycleEstimateAssumptions {
  modelBasis: string;
  startingTemperatureC: number;
  startingTemperatureSource: StartingTemperatureSource;
  targetHeatTemperatureC: number;
  referenceFrequencyHz: number;
  selectedPowerSourceFrequencyHz: number;
  frequencyFactor: number;
  referencePipeOutsideDiameterMm: number;
  referencePipeWallThicknessMm: number;
  selectedCoilHeatingResponseCelsiusMeterPerKwMinute: number | null;
}

export interface CooldownEstimateAssumptions {
  modelBasis: string;
  baseCooldownRateCelsiusPerMinute: number;
  referenceFrequencyHz: number;
  selectedPowerSourceFrequencyHz: number;
  frequencyFactor: number;
  referenceHeatSoakHeatingCycleTimeMinutes: number;
  heatingCycleTimeMinutes: number;
  pipeWallThicknessMm: number;
  referencePipeWallThicknessMm: number;
  targetHeatTemperatureC: number;
  subsequentProcessTemperatureC: number;
  temperatureDropC: number;
  heatSoakFactor: number;
}

export interface PowerUtilizationEstimateAssumptions {
  referenceFrequencyHz: number;
  referencePowerSourceVoltageV: number;
  referencePowerSourceCurrentA: number;
  referencePipeOutsideDiameterMm: number;
  referencePipeWallThicknessMm: number;
  referenceEffectiveCoilInsideDiameterMm: number;
  referenceNumberOfTurns: number;
  referenceBottomEndWidthMm: number;
  referenceTurnSpacingMm: number;
  modelCoefficients: RecommendationModelCoefficients;
}

export interface RecommendationAssumptions {
  coilClearanceMm: number;
  requiredOverhangMm: number;
  marginalRecommendationRules: MarginalRecommendationRules;
  advisoryThresholds: AdvisoryThresholds;
  frequencyCalibrationCurve: FrequencyCalibrationPoint[];
  powerUtilizationEstimate: PowerUtilizationEstimateAssumptions;
  heatingCycleEstimate: HeatingCycleEstimateAssumptions;
  cooldownEstimate?: CooldownEstimateAssumptions;
}

export interface RecommendedSetup {
  selectedPowerSource: PowerSource;
  optimumCoilRecommendation: OptimumCoilRecommendation;
  coilRecommendationComparison: CoilRecommendationComparison;
  selectedCoilConfiguration: SelectedCoilConfiguration | null;
  recommendationStatus: RecommendationStatus;
  recommendationReason: string;
  advisoryCauses: RecommendationAdvisoryCause[];
  advisoryActions: RecommendationAdvisoryAction[];
  operatingElectricalValues: OperatingElectricalValues | null;
  deliveredPowerKw: number | null;
  powerUtilization: number | null;
  heatingCycleTimeMinutes: number | null;
  cooldownTimeMinutes?: number;
  calculations: RecommendationCalculations;
  catalogueEvaluation: CatalogueEvaluation;
  assumptions: RecommendationAssumptions;
}

export const standardEquipmentCatalogue: StandardEquipmentCatalogue =
  standardEquipmentCatalogueJson;

export const defaultRecommendationOptions: RecommendationOptions =
  toRecommendationOptions(recommendationAssumptionsJson);

export const defaultRecommendationAssumptionSet = defaultRecommendationOptions;
const defaultMarginalRecommendationRules: MarginalRecommendationRules = {
  minimumCoilFrameSizeMarginMm: 25,
  minimumCoilCoverageMarginMm: 25,
  powerUtilizationBoundaryMargin: 0.05,
};
const referenceHeatingPipeOutsideDiameterMm = 610;
const referenceHeatingPipeWallThicknessMm = 12.7;
const referencePowerSourceVoltageV = 400;
const referencePowerSourceCurrentA = 144;
const referenceEffectiveCoilInsideDiameterMm = 718;
const referenceNumberOfTurns = 6;
const referenceBottomEndWidthMm = 480;
const referenceTurnSpacingMm = 75;
const baseCooldownRateCelsiusPerMinute = 5;
const referenceHeatSoakHeatingCycleTimeMinutes = 1.2;
const empiricalAdvisoryModelBasis =
  "Empirical advisory estimate, not first-principles skin-effect modelling.";

export function recommendSetup(input: RecommendSetupInput): RecommendedSetup {
  validateRecommendationRequest(input);

  const recommendationOptions = resolveRecommendationOptions(input);
  const catalogue = normalizeStandardEquipmentCatalogue(
    input.standardEquipmentCatalogue ?? standardEquipmentCatalogue,
    recommendationOptions,
  );
  const coilClearanceMm = recommendationOptions.coilClearanceMm;
  const requiredOverhangMm = recommendationOptions.requiredOverhangMm;
  const advisoryThresholds = resolveAdvisoryThresholds(
    input,
    recommendationOptions,
  );
  const marginalRecommendationRules =
    toMarginalRecommendationRules(advisoryThresholds);

  const calculations = calculateRecommendationInputs(
    input.fieldJointGeometry,
    coilClearanceMm,
    requiredOverhangMm,
  );
  const optimumCoilRecommendation = calculateOptimumCoilRecommendation(
    calculations.requiredCoilCoverageMm,
    recommendationOptions,
  );

  const selectedPowerSource = selectPowerSource(
    catalogue.powerSources,
    input.selectedPowerSourceId,
    input.availablePowerSourceIds,
  );
  const catalogueEvaluation = evaluateStandardCoilCatalogue(
    catalogue.coilConfigurations,
    calculations.requiredCoilInsideDiameterMm,
    calculations.requiredCoilCoverageMm,
  );

  if (!selectedPowerSource) {
    throw new Error("No available power source was found in the standard equipment catalogue.");
  }

  if (catalogue.coilConfigurations.length === 0) {
    return {
      selectedPowerSource,
      optimumCoilRecommendation,
      coilRecommendationComparison: buildCoilRecommendationComparison(
        optimumCoilRecommendation,
        null,
      ),
      selectedCoilConfiguration: null,
      recommendationStatus: "not supported",
      recommendationReason:
        "No valid standard coil configuration was found in the standard equipment catalogue, so no calculated recommendation can be produced.",
      advisoryCauses: [],
      advisoryActions: [],
      operatingElectricalValues: null,
      deliveredPowerKw: null,
      powerUtilization: null,
      heatingCycleTimeMinutes: null,
      calculations: {
        ...calculations,
        heatingLengthMm: null,
      },
      catalogueEvaluation,
      assumptions: buildRecommendationAssumptions(
        input,
        recommendationOptions,
        coilClearanceMm,
        requiredOverhangMm,
        marginalRecommendationRules,
        selectedPowerSource,
        null,
        null,
      ),
    };
  }

  const selectedCoilConfiguration = selectBestCatalogueMatchedCoilConfiguration(
    catalogue.coilConfigurations,
    calculations.requiredCoilInsideDiameterMm,
    calculations.requiredCoilCoverageMm,
    selectedPowerSource,
    input.fieldJointGeometry,
    optimumCoilRecommendation,
    recommendationOptions,
  );

  const operatingPoint = calculateOperatingPoint(
    selectedCoilConfiguration,
    selectedPowerSource,
    input.fieldJointGeometry,
    recommendationOptions,
  );
  const heatingCycleTimeMinutes = estimateHeatingCycleTimeMinutes(
    input.fieldJointGeometry,
    input.heating,
    selectedCoilConfiguration.heatingLengthMm,
    operatingPoint.deliveredPowerKw,
    selectedCoilConfiguration.heatingResponseCelsiusMeterPerKwMinute,
    selectedPowerSource,
    recommendationOptions,
  );
  const cooldownTimeMinutes = estimateCooldownTimeMinutes(
    input.fieldJointGeometry,
    input.heating,
    input.applicationProduct,
    selectedPowerSource,
    recommendationOptions,
    heatingCycleTimeMinutes,
  );
  const advisoryCauses = buildAdvisoryCauses(
    operatingPoint.powerUtilization,
    operatingPoint.operatingElectricalValues,
    selectedPowerSource.operatingLimits,
    selectedCoilConfiguration,
    calculations,
    advisoryThresholds,
  );
  const advisoryActions = buildAdvisoryActions(advisoryCauses);
  const { recommendationStatus, recommendationReason } = classifyRecommendation(
    advisoryCauses,
  );

  return {
    selectedPowerSource,
    optimumCoilRecommendation,
    coilRecommendationComparison: buildCoilRecommendationComparison(
      optimumCoilRecommendation,
      selectedCoilConfiguration,
    ),
    selectedCoilConfiguration: {
      id: selectedCoilConfiguration.id,
      name: selectedCoilConfiguration.name,
      ...(selectedCoilConfiguration.coilFrameId === undefined
        ? {}
        : { coilFrameId: selectedCoilConfiguration.coilFrameId }),
      ...(selectedCoilConfiguration.bottomEndId === undefined
        ? {}
        : { bottomEndId: selectedCoilConfiguration.bottomEndId }),
      ...(selectedCoilConfiguration.selectedCoilFrame === undefined
        ? {}
        : { coilFrame: selectedCoilConfiguration.selectedCoilFrame }),
      ...(selectedCoilConfiguration.selectedBottomEnd === undefined
        ? {}
        : { bottomEnd: selectedCoilConfiguration.selectedBottomEnd }),
      ...(selectedCoilConfiguration.coilFrameSizeInches === undefined
        ? {}
        : { coilFrameSizeInches: selectedCoilConfiguration.coilFrameSizeInches }),
      ...(selectedCoilConfiguration.coilFrameInsideDiameterMm === undefined
        ? {}
        : {
            coilFrameInsideDiameterMm:
              selectedCoilConfiguration.coilFrameInsideDiameterMm,
          }),
      effectiveCoilInsideDiameterMm:
        selectedCoilConfiguration.effectiveCoilInsideDiameterMm,
      coilFrameSizeMm: selectedCoilConfiguration.coilFrameSizeMm,
      numberOfTurns: selectedCoilConfiguration.numberOfTurns,
      turnSpacingMm: selectedCoilConfiguration.turnSpacingMm,
      coilCoverageMm: selectedCoilConfiguration.coilCoverageMm,
      ...(selectedCoilConfiguration.selectedBottomEnd?.coilSectionMm === undefined
        ? {}
        : {
            coilSectionMm:
              selectedCoilConfiguration.selectedBottomEnd.coilSectionMm,
          }),
      heatingLengthMm: selectedCoilConfiguration.heatingLengthMm,
    },
    recommendationStatus,
    recommendationReason,
    advisoryCauses,
    advisoryActions,
    operatingElectricalValues: operatingPoint.operatingElectricalValues,
    deliveredPowerKw: operatingPoint.deliveredPowerKw,
    powerUtilization: operatingPoint.powerUtilization,
    heatingCycleTimeMinutes,
    ...(cooldownTimeMinutes === undefined ? {} : { cooldownTimeMinutes }),
    calculations: {
      ...calculations,
      heatingLengthMm: selectedCoilConfiguration.heatingLengthMm,
    },
    catalogueEvaluation,
    assumptions: buildRecommendationAssumptions(
      input,
      recommendationOptions,
      coilClearanceMm,
      requiredOverhangMm,
      marginalRecommendationRules,
      selectedPowerSource,
      selectedCoilConfiguration,
      heatingCycleTimeMinutes,
    ),
  };
}

function buildRecommendationAssumptions(
  input: RecommendSetupInput,
  recommendationOptions: RecommendationOptions,
  coilClearanceMm: number,
  requiredOverhangMm: number,
  marginalRecommendationRules: MarginalRecommendationRules,
  selectedPowerSource: PowerSource,
  selectedCoilConfiguration: CatalogueMatchedCoilConfiguration | null,
  heatingCycleTimeMinutes: number | null | undefined,
): RecommendationAssumptions {
  const cooldownEstimate = buildCooldownEstimateAssumptions(
    input,
    recommendationOptions,
    selectedPowerSource,
    heatingCycleTimeMinutes,
  );

  return {
    coilClearanceMm,
    requiredOverhangMm,
    marginalRecommendationRules: {
      ...marginalRecommendationRules,
    },
    advisoryThresholds: {
      ...recommendationOptions.advisoryThresholds,
    },
    frequencyCalibrationCurve: recommendationOptions.frequencyCalibrationCurve.map(
      (point) => ({ ...point }),
    ),
    powerUtilizationEstimate: {
      referenceFrequencyHz: recommendationOptions.referenceFrequencyHz,
      referencePowerSourceVoltageV,
      referencePowerSourceCurrentA,
      referencePipeOutsideDiameterMm: referenceHeatingPipeOutsideDiameterMm,
      referencePipeWallThicknessMm: referenceHeatingPipeWallThicknessMm,
      referenceEffectiveCoilInsideDiameterMm,
      referenceNumberOfTurns,
      referenceBottomEndWidthMm,
      referenceTurnSpacingMm,
      modelCoefficients: {
        ...recommendationOptions.modelCoefficients,
      },
    },
    heatingCycleEstimate: {
      modelBasis: empiricalAdvisoryModelBasis,
      startingTemperatureC: resolveStartingTemperatureC(input.heating),
      startingTemperatureSource: resolveStartingTemperatureSource(input.heating),
      targetHeatTemperatureC: input.heating.targetHeatTemperatureC,
      referenceFrequencyHz: recommendationOptions.referenceFrequencyHz,
      selectedPowerSourceFrequencyHz: selectedPowerSource.powerSourceFrequencyHz,
      frequencyFactor: calculateFrequencyFactor(
        selectedPowerSource.powerSourceFrequencyHz,
        recommendationOptions.frequencyCalibrationCurve,
        "heatingCycleFactor",
      ),
      referencePipeOutsideDiameterMm: referenceHeatingPipeOutsideDiameterMm,
      referencePipeWallThicknessMm: referenceHeatingPipeWallThicknessMm,
      selectedCoilHeatingResponseCelsiusMeterPerKwMinute:
        selectedCoilConfiguration?.heatingResponseCelsiusMeterPerKwMinute ??
        null,
    },
    ...(cooldownEstimate === undefined ? {} : { cooldownEstimate }),
  };
}

function buildCooldownEstimateAssumptions(
  input: RecommendSetupInput,
  recommendationOptions: RecommendationOptions,
  selectedPowerSource: PowerSource,
  heatingCycleTimeMinutes: number | null | undefined,
): CooldownEstimateAssumptions | undefined {
  const subsequentProcessTemperatureC =
    input.applicationProduct?.subsequentProcessTemperatureC;

  if (
    subsequentProcessTemperatureC === undefined ||
    heatingCycleTimeMinutes === undefined ||
    heatingCycleTimeMinutes === null
  ) {
    return undefined;
  }

  const temperatureDropC = Math.max(
    input.heating.targetHeatTemperatureC - subsequentProcessTemperatureC,
    0,
  );

  return {
    modelBasis: empiricalAdvisoryModelBasis,
    baseCooldownRateCelsiusPerMinute,
    referenceFrequencyHz: recommendationOptions.referenceFrequencyHz,
    selectedPowerSourceFrequencyHz: selectedPowerSource.powerSourceFrequencyHz,
    frequencyFactor: calculateFrequencyFactor(
      selectedPowerSource.powerSourceFrequencyHz,
      recommendationOptions.frequencyCalibrationCurve,
      "cooldownFactor",
    ),
    referenceHeatSoakHeatingCycleTimeMinutes,
    heatingCycleTimeMinutes,
    pipeWallThicknessMm: input.fieldJointGeometry.pipeWallThicknessMm,
    referencePipeWallThicknessMm: referenceHeatingPipeWallThicknessMm,
    targetHeatTemperatureC: input.heating.targetHeatTemperatureC,
    subsequentProcessTemperatureC,
    temperatureDropC,
    heatSoakFactor: calculateHeatSoakFactor(
      heatingCycleTimeMinutes,
      input.fieldJointGeometry.pipeWallThicknessMm,
      recommendationOptions,
    ),
  };
}

function validateRecommendationRequest(input: RecommendSetupInput): void {
  const errors: string[] = [];

  validateFieldJointGeometry(input.fieldJointGeometry, errors);
  validateHeatingInput(input.heating, errors);

  if (
    input.coilClearanceMm !== undefined &&
    !isNonNegativeFiniteNumber(input.coilClearanceMm)
  ) {
    errors.push("coilClearanceMm must be a finite non-negative metric value.");
  }

  if (
    input.requiredOverhangMm !== undefined &&
    !isNonNegativeFiniteNumber(input.requiredOverhangMm)
  ) {
    errors.push("requiredOverhangMm must be a finite non-negative metric value.");
  }

  if (input.applicationProduct !== undefined) {
    validateApplicationProductInput(input.applicationProduct, errors);
  }

  if (input.marginalRecommendationRules !== undefined) {
    validateMarginalRecommendationRules(
      input.marginalRecommendationRules,
      errors,
    );
  }

  if (input.recommendationOptions !== undefined) {
    validateRecommendationOptions(input.recommendationOptions, errors);
  }

  if (errors.length > 0) {
    throw new Error(`Invalid recommendation request: ${errors.join(" ")}`);
  }
}

function resolveRecommendationOptions(
  input: RecommendSetupInput,
): RecommendationOptions {
  const modelCoefficients = {
    ...defaultRecommendationOptions.advancedCalibration.modelCoefficients,
    ...input.recommendationOptions?.advancedCalibration?.modelCoefficients,
    ...input.recommendationOptions?.modelCoefficients,
  };

  return {
    ...defaultRecommendationOptions,
    ...input.recommendationOptions,
    turnBounds: {
      ...defaultRecommendationOptions.turnBounds,
      ...input.recommendationOptions?.turnBounds,
    },
    frequencyCalibrationCurve:
      input.recommendationOptions?.frequencyCalibrationCurve?.map((point) => ({
        ...point,
      })) ??
      defaultRecommendationOptions.frequencyCalibrationCurve.map((point) => ({
        ...point,
      })),
    advisoryThresholds: {
      ...defaultRecommendationOptions.advisoryThresholds,
      ...input.recommendationOptions?.advisoryThresholds,
    },
    advancedCalibration: {
      modelCoefficients,
    },
    modelCoefficients,
    ...(input.coilClearanceMm === undefined
      ? {}
      : { coilClearanceMm: input.coilClearanceMm }),
    ...(input.requiredOverhangMm === undefined
      ? {}
      : { requiredOverhangMm: input.requiredOverhangMm }),
  };
}

function toRecommendationOptions(
  assumptions: RecommendationOptionsInput,
): RecommendationOptions {
  const modelCoefficients = {
    ...assumptions.advancedCalibration?.modelCoefficients,
    ...assumptions.modelCoefficients,
  } as RecommendationModelCoefficients;

  return {
    referenceFrequencyHz: assumptions.referenceFrequencyHz ?? 480,
    preferredTurnSpacingMm: assumptions.preferredTurnSpacingMm ?? 75,
    turnBounds: {
      minimumTurns: assumptions.turnBounds?.minimumTurns ?? 4,
      maximumTurns: assumptions.turnBounds?.maximumTurns ?? 16,
    },
    coilClearanceMm: assumptions.coilClearanceMm ?? 40,
    requiredOverhangMm: assumptions.requiredOverhangMm ?? 50,
    tieBarDiameterMm: assumptions.tieBarDiameterMm ?? 16,
    frequencyCalibrationCurve:
      assumptions.frequencyCalibrationCurve?.map((point) => ({ ...point })) ?? [],
    advisoryThresholds: {
      lowPowerUtilization: assumptions.advisoryThresholds?.lowPowerUtilization ?? 0.6,
      minimumCoilFrameSizeMarginMm:
        assumptions.advisoryThresholds?.minimumCoilFrameSizeMarginMm ?? 25,
      minimumCoilCoverageMarginMm:
        assumptions.advisoryThresholds?.minimumCoilCoverageMarginMm ?? 25,
      powerUtilizationBoundaryMargin:
        assumptions.advisoryThresholds?.powerUtilizationBoundaryMargin ?? 0.05,
    },
    advancedCalibration: {
      modelCoefficients,
    },
    modelCoefficients,
  };
}

function resolveAdvisoryThresholds(
  input: RecommendSetupInput,
  recommendationOptions: RecommendationOptions,
): AdvisoryThresholds {
  if (input.marginalRecommendationRules === undefined) {
    return recommendationOptions.advisoryThresholds;
  }

  return {
    lowPowerUtilization:
      recommendationOptions.advisoryThresholds.lowPowerUtilization,
    minimumCoilFrameSizeMarginMm:
      input.marginalRecommendationRules.minimumCoilFrameSizeMarginMm,
    minimumCoilCoverageMarginMm:
      input.marginalRecommendationRules.minimumCoilCoverageMarginMm,
    powerUtilizationBoundaryMargin:
      input.marginalRecommendationRules.powerUtilizationBoundaryMargin,
  };
}

function toMarginalRecommendationRules(
  advisoryThresholds: AdvisoryThresholds,
): MarginalRecommendationRules {
  return {
    minimumCoilFrameSizeMarginMm:
      advisoryThresholds.minimumCoilFrameSizeMarginMm,
    minimumCoilCoverageMarginMm:
      advisoryThresholds.minimumCoilCoverageMarginMm,
    powerUtilizationBoundaryMargin:
      advisoryThresholds.powerUtilizationBoundaryMargin,
  };
}

function validateFieldJointGeometry(
  fieldJointGeometry: FieldJointGeometryInput,
  errors: string[],
): void {
  if (!isRecord(fieldJointGeometry)) {
    errors.push("fieldJointGeometry is required.");
    return;
  }

  if (!isPositiveFiniteNumber(fieldJointGeometry.pipeOutsideDiameterMm)) {
    errors.push(
      "fieldJointGeometry.pipeOutsideDiameterMm must be a finite positive metric value.",
    );
  }

  if (!isPositiveFiniteNumber(fieldJointGeometry.pipeWallThicknessMm)) {
    errors.push(
      "fieldJointGeometry.pipeWallThicknessMm must be a finite positive metric value.",
    );
  }

  if (
    !isNonNegativeFiniteNumber(fieldJointGeometry.factoryCoatingThicknessMm)
  ) {
    errors.push(
      "fieldJointGeometry.factoryCoatingThicknessMm must be a finite non-negative metric value.",
    );
  }

  if (!isNonNegativeFiniteNumber(fieldJointGeometry.sideACutbackMm)) {
    errors.push(
      "fieldJointGeometry.sideACutbackMm must be a finite non-negative metric value.",
    );
  }

  if (!isNonNegativeFiniteNumber(fieldJointGeometry.sideBCutbackMm)) {
    errors.push(
      "fieldJointGeometry.sideBCutbackMm must be a finite non-negative metric value.",
    );
  }
}

function validateHeatingInput(
  heating: HeatingInput,
  errors: string[],
): void {
  if (!isRecord(heating)) {
    errors.push("heating is required.");
    return;
  }

  if (!isFiniteNumber(heating.targetHeatTemperatureC)) {
    errors.push("heating.targetHeatTemperatureC must be finite.");
  }

  if (
    heating.startingTemperatureC !== undefined &&
    !isFiniteNumber(heating.startingTemperatureC)
  ) {
    errors.push("heating.startingTemperatureC must be finite when supplied.");
  }

  if (
    heating.ambientTemperatureC !== undefined &&
    !isFiniteNumber(heating.ambientTemperatureC)
  ) {
    errors.push("heating.ambientTemperatureC must be finite when supplied.");
  }
}

function validateApplicationProductInput(
  applicationProduct: ApplicationProductInput,
  errors: string[],
): void {
  if (!isRecord(applicationProduct)) {
    errors.push("applicationProduct must be a usable application product.");
    return;
  }

  if (!hasNonEmptyText(applicationProduct.id)) {
    errors.push("applicationProduct.id must be usable.");
  }

  if (!hasNonEmptyText(applicationProduct.name)) {
    errors.push("applicationProduct.name must be usable.");
  }

  if (
    applicationProduct.subsequentProcessTemperatureC !== undefined &&
    !isFiniteNumber(applicationProduct.subsequentProcessTemperatureC)
  ) {
    errors.push(
      "applicationProduct.subsequentProcessTemperatureC must be finite when supplied.",
    );
  }
}

function validateMarginalRecommendationRules(
  marginalRecommendationRules: MarginalRecommendationRules,
  errors: string[],
): void {
  if (!isRecord(marginalRecommendationRules)) {
    errors.push("marginalRecommendationRules must be usable.");
    return;
  }

  if (
    !isNonNegativeFiniteNumber(
      marginalRecommendationRules.minimumCoilFrameSizeMarginMm,
    )
  ) {
    errors.push(
      "marginalRecommendationRules.minimumCoilFrameSizeMarginMm must be finite and non-negative.",
    );
  }

  if (
    !isNonNegativeFiniteNumber(
      marginalRecommendationRules.minimumCoilCoverageMarginMm,
    )
  ) {
    errors.push(
      "marginalRecommendationRules.minimumCoilCoverageMarginMm must be finite and non-negative.",
    );
  }

  if (
    !isNonNegativeFiniteNumber(
      marginalRecommendationRules.powerUtilizationBoundaryMargin,
    )
  ) {
    errors.push(
      "marginalRecommendationRules.powerUtilizationBoundaryMargin must be finite and non-negative.",
    );
  }
}

function validateRecommendationOptions(
  recommendationOptions: RecommendationOptionsInput,
  errors: string[],
): void {
  if (!isRecord(recommendationOptions)) {
    errors.push("recommendationOptions must be usable.");
    return;
  }

  if (
    recommendationOptions.referenceFrequencyHz !== undefined &&
    !isPositiveFiniteNumber(recommendationOptions.referenceFrequencyHz)
  ) {
    errors.push("recommendationOptions.referenceFrequencyHz must be finite and positive.");
  }

  if (
    recommendationOptions.preferredTurnSpacingMm !== undefined &&
    !isPositiveFiniteNumber(recommendationOptions.preferredTurnSpacingMm)
  ) {
    errors.push(
      "recommendationOptions.preferredTurnSpacingMm must be finite and positive.",
    );
  }

  if (
    recommendationOptions.coilClearanceMm !== undefined &&
    !isNonNegativeFiniteNumber(recommendationOptions.coilClearanceMm)
  ) {
    errors.push(
      "recommendationOptions.coilClearanceMm must be a finite non-negative metric value.",
    );
  }

  if (
    recommendationOptions.requiredOverhangMm !== undefined &&
    !isNonNegativeFiniteNumber(recommendationOptions.requiredOverhangMm)
  ) {
    errors.push(
      "recommendationOptions.requiredOverhangMm must be a finite non-negative metric value.",
    );
  }

  if (
    recommendationOptions.tieBarDiameterMm !== undefined &&
    !isNonNegativeFiniteNumber(recommendationOptions.tieBarDiameterMm)
  ) {
    errors.push(
      "recommendationOptions.tieBarDiameterMm must be a finite non-negative metric value.",
    );
  }

  if (recommendationOptions.turnBounds !== undefined) {
    validateTurnBounds(recommendationOptions.turnBounds, errors);
  }

  if (recommendationOptions.frequencyCalibrationCurve !== undefined) {
    validateFrequencyCalibrationCurve(
      recommendationOptions.frequencyCalibrationCurve,
      errors,
    );
  }

  if (recommendationOptions.advisoryThresholds !== undefined) {
    validateAdvisoryThresholds(recommendationOptions.advisoryThresholds, errors);
  }

  if (recommendationOptions.advancedCalibration !== undefined) {
    validateAdvancedRecommendationCalibration(
      recommendationOptions.advancedCalibration,
      errors,
    );
  }

  if (recommendationOptions.modelCoefficients !== undefined) {
    validateRecommendationModelCoefficients(
      recommendationOptions.modelCoefficients,
      errors,
      "recommendationOptions.modelCoefficients",
    );
  }
}

function validateTurnBounds(
  turnBounds: unknown,
  errors: string[],
): void {
  if (!isRecord(turnBounds)) {
    errors.push("recommendationOptions.turnBounds must be usable.");
    return;
  }

  const minimumTurns = turnBounds.minimumTurns;
  const maximumTurns = turnBounds.maximumTurns;

  if (minimumTurns !== undefined && !isPositiveInteger(minimumTurns)) {
    errors.push(
      "recommendationOptions.turnBounds.minimumTurns must be a positive integer.",
    );
  }

  if (maximumTurns !== undefined && !isPositiveInteger(maximumTurns)) {
    errors.push(
      "recommendationOptions.turnBounds.maximumTurns must be a positive integer.",
    );
  }

  if (
    minimumTurns !== undefined &&
    maximumTurns !== undefined &&
    isPositiveInteger(minimumTurns) &&
    isPositiveInteger(maximumTurns) &&
    minimumTurns > maximumTurns
  ) {
    errors.push(
      "recommendationOptions.turnBounds.minimumTurns must be less than or equal to maximumTurns.",
    );
  }
}

function validateRecommendationModelCoefficients(
  modelCoefficients: unknown,
  errors: string[],
  path = "recommendationOptions.advancedCalibration.modelCoefficients",
): void {
  if (!isRecord(modelCoefficients)) {
    errors.push(`${path} must be usable.`);
    return;
  }

  for (const key of [
    "powerUtilizationBase",
    "powerUtilizationFrequencyCoefficient",
    "powerUtilizationVoltageCoefficient",
    "powerUtilizationCurrentCoefficient",
    "powerUtilizationPipeOutsideDiameterCoefficient",
    "powerUtilizationPipeWallThicknessCoefficient",
    "powerUtilizationFrameDiameterCoefficient",
    "powerUtilizationTurnsCoefficient",
    "powerUtilizationBottomEndWidthCoefficient",
    "powerUtilizationTurnSpacingCoefficient",
    "heatingCycleFrequencyCoefficient",
    "cooldownFrequencyCoefficient",
    "cooldownHeatingCycleHeatSoakCoefficient",
    "cooldownPipeWallThicknessCoefficient",
  ] as const) {
    if (
      modelCoefficients[key] !== undefined &&
      !isFiniteNumber(modelCoefficients[key])
    ) {
      errors.push(`${path}.${key} must be finite.`);
    }
  }
}

function validateFrequencyCalibrationCurve(
  frequencyCalibrationCurve: unknown,
  errors: string[],
): void {
  if (!Array.isArray(frequencyCalibrationCurve)) {
    errors.push("recommendationOptions.frequencyCalibrationCurve must be a list.");
    return;
  }

  if (frequencyCalibrationCurve.length < 2) {
    errors.push(
      "recommendationOptions.frequencyCalibrationCurve must include at least two points.",
    );
    return;
  }

  let previousFrequencyHz = Number.NEGATIVE_INFINITY;

  frequencyCalibrationCurve.forEach((point, index) => {
    const path = `recommendationOptions.frequencyCalibrationCurve[${index}]`;

    if (!isRecord(point)) {
      errors.push(`${path} must be usable.`);
      return;
    }

    if (!isPositiveFiniteNumber(point.frequencyHz)) {
      errors.push(`${path}.frequencyHz must be finite and positive.`);
    } else if (point.frequencyHz <= previousFrequencyHz) {
      errors.push(
        "recommendationOptions.frequencyCalibrationCurve frequencies must increase.",
      );
    } else {
      previousFrequencyHz = point.frequencyHz;
    }

    if (!isPositiveFiniteNumber(point.heatingCycleFactor)) {
      errors.push(`${path}.heatingCycleFactor must be finite and positive.`);
    }

    if (!isPositiveFiniteNumber(point.cooldownFactor)) {
      errors.push(`${path}.cooldownFactor must be finite and positive.`);
    }
  });
}

function validateAdvisoryThresholds(
  advisoryThresholds: unknown,
  errors: string[],
): void {
  if (!isRecord(advisoryThresholds)) {
    errors.push("recommendationOptions.advisoryThresholds must be usable.");
    return;
  }

  if (
    advisoryThresholds.lowPowerUtilization !== undefined &&
    (!isFiniteNumber(advisoryThresholds.lowPowerUtilization) ||
      advisoryThresholds.lowPowerUtilization < 0 ||
      advisoryThresholds.lowPowerUtilization > 1)
  ) {
    errors.push(
      "recommendationOptions.advisoryThresholds.lowPowerUtilization must be between 0 and 1.",
    );
  }

  if (
    advisoryThresholds.minimumCoilFrameSizeMarginMm !== undefined &&
    !isNonNegativeFiniteNumber(
      advisoryThresholds.minimumCoilFrameSizeMarginMm,
    )
  ) {
    errors.push(
      "recommendationOptions.advisoryThresholds.minimumCoilFrameSizeMarginMm must be finite and non-negative.",
    );
  }

  if (
    advisoryThresholds.minimumCoilCoverageMarginMm !== undefined &&
    !isNonNegativeFiniteNumber(
      advisoryThresholds.minimumCoilCoverageMarginMm,
    )
  ) {
    errors.push(
      "recommendationOptions.advisoryThresholds.minimumCoilCoverageMarginMm must be finite and non-negative.",
    );
  }

  if (
    advisoryThresholds.powerUtilizationBoundaryMargin !== undefined &&
    !isNonNegativeFiniteNumber(
      advisoryThresholds.powerUtilizationBoundaryMargin,
    )
  ) {
    errors.push(
      "recommendationOptions.advisoryThresholds.powerUtilizationBoundaryMargin must be finite and non-negative.",
    );
  }
}

function validateAdvancedRecommendationCalibration(
  advancedCalibration: unknown,
  errors: string[],
): void {
  if (!isRecord(advancedCalibration)) {
    errors.push("recommendationOptions.advancedCalibration must be usable.");
    return;
  }

  if (advancedCalibration.modelCoefficients !== undefined) {
    validateRecommendationModelCoefficients(
      advancedCalibration.modelCoefficients,
      errors,
    );
  }
}

function normalizeStandardEquipmentCatalogue(
  catalogue: StandardEquipmentCatalogue,
  recommendationOptions: RecommendationOptions,
): NormalizedStandardEquipmentCatalogue {
  const powerSources = catalogue.powerSources.filter(isValidPowerSource);
  const coilFrames = (catalogue.coilFrames ?? []).filter(isValidCoilFrame);
  const bottomEnds = (catalogue.bottomEnds ?? [])
    .filter(isValidBottomEnd)
    .map(toNormalizedBottomEnd);
  const generatedCoilConfigurations = generateCatalogueMatchedCoilConfigurations(
    coilFrames,
    bottomEnds,
    recommendationOptions.tieBarDiameterMm,
  );

  return {
    powerSources,
    coilFrames,
    bottomEnds,
    coilConfigurations:
      generatedCoilConfigurations.length > 0
        ? generatedCoilConfigurations
        : (catalogue.coilConfigurations ?? [])
            .filter(isValidCoilConfiguration)
            .map(toLegacyCatalogueMatchedCoilConfiguration),
  };
}

function isValidPowerSource(powerSource: unknown): powerSource is PowerSource {
  if (!isRecord(powerSource)) {
    return false;
  }

  return (
    hasNonEmptyText(powerSource.id) &&
    hasNonEmptyText(powerSource.name) &&
    isPositiveFiniteNumber(powerSource.powerSourceRatingKva) &&
    isPositiveFiniteNumber(powerSource.powerSourceFrequencyHz) &&
    isPositiveFiniteNumber(powerSource.maximumPowerSourceVoltageV) &&
    isPositiveFiniteNumber(powerSource.maximumPowerSourceCurrentA) &&
    isValidPowerSourceOperatingLimits(powerSource.operatingLimits)
  );
}

function isValidPowerSourceOperatingLimits(
  operatingLimits: unknown,
): operatingLimits is PowerSourceOperatingLimits {
  if (!isRecord(operatingLimits)) {
    return false;
  }

  return (
    isFiniteNumber(operatingLimits.minimumPowerUtilization) &&
    isFiniteNumber(operatingLimits.maximumPowerUtilization) &&
    operatingLimits.minimumPowerUtilization >= 0 &&
    operatingLimits.maximumPowerUtilization <= 1 &&
    operatingLimits.minimumPowerUtilization <=
      operatingLimits.maximumPowerUtilization
  );
}

function isValidCoilConfiguration(
  coilConfiguration: unknown,
): coilConfiguration is CoilConfiguration {
  if (!isRecord(coilConfiguration)) {
    return false;
  }

  return (
    hasNonEmptyText(coilConfiguration.id) &&
    hasNonEmptyText(coilConfiguration.name) &&
    isPositiveFiniteNumber(coilConfiguration.coilFrameSizeMm) &&
    isPositiveInteger(coilConfiguration.numberOfTurns) &&
    isPositiveFiniteNumber(coilConfiguration.turnSpacingMm) &&
    isPositiveFiniteNumber(coilConfiguration.coilCoverageMm) &&
    isPositiveFiniteNumber(
      coilConfiguration.heatingResponseCelsiusMeterPerKwMinute,
    )
  );
}

function isValidCoilFrame(coilFrame: unknown): coilFrame is CoilFrame {
  if (!isRecord(coilFrame)) {
    return false;
  }

  return (
    hasNonEmptyText(coilFrame.id) &&
    hasNonEmptyText(coilFrame.name) &&
    isPositiveFiniteNumber(coilFrame.coilFrameSizeInches) &&
    isPositiveFiniteNumber(coilFrame.coilFrameInsideDiameterMm)
  );
}

function isValidBottomEnd(bottomEnd: unknown): bottomEnd is BottomEnd {
  if (!isRecord(bottomEnd)) {
    return false;
  }

  if (
    !Array.isArray(bottomEnd.contactCenterSpacingsMm) ||
    !isPositiveInteger(bottomEnd.numberOfTurns) ||
    bottomEnd.contactCenterSpacingsMm.length !== bottomEnd.numberOfTurns - 1 ||
    !bottomEnd.contactCenterSpacingsMm.every(isPositiveFiniteNumber)
  ) {
    return false;
  }

  return (
    hasNonEmptyText(bottomEnd.id) &&
    hasNonEmptyText(bottomEnd.name) &&
    bottomEnd.numberOfTurns > 1 &&
    isNonNegativeFiniteNumber(bottomEnd.firstContactEdgeOffsetMm) &&
    isNonNegativeFiniteNumber(bottomEnd.lastContactEdgeOffsetMm) &&
    isPositiveFiniteNumber(bottomEnd.bottomEndWidthMm) &&
    bottomEnd.bottomEndWidthMm >
      bottomEnd.firstContactEdgeOffsetMm + bottomEnd.lastContactEdgeOffsetMm &&
    isPositiveFiniteNumber(bottomEnd.heatingResponseCelsiusMeterPerKwMinute)
  );
}

function generateCatalogueMatchedCoilConfigurations(
  coilFrames: CoilFrame[],
  bottomEnds: NormalizedBottomEnd[],
  tieBarDiameterMm: number,
): CatalogueMatchedCoilConfiguration[] {
  return coilFrames.flatMap((coilFrame) =>
    bottomEnds.map((bottomEnd) => ({
      id: `${coilFrame.id}-${bottomEnd.id}`,
      name: `${coilFrame.name}, ${bottomEnd.name}`,
      coilFrameId: coilFrame.id,
      bottomEndId: bottomEnd.id,
      selectedCoilFrame: coilFrame,
      selectedBottomEnd: bottomEnd,
      coilFrameSizeInches: coilFrame.coilFrameSizeInches,
      coilFrameInsideDiameterMm: coilFrame.coilFrameInsideDiameterMm,
      coilFrameSizeMm: coilFrame.coilFrameInsideDiameterMm,
      effectiveCoilInsideDiameterMm:
        coilFrame.coilFrameInsideDiameterMm - 2 * tieBarDiameterMm,
      numberOfTurns: bottomEnd.numberOfTurns,
      turnSpacingMm: bottomEnd.generatedTurnSpacingMm,
      coilCoverageMm: bottomEnd.bottomEndWidthMm,
      heatingLengthMm: bottomEnd.coilSectionMm,
      heatingResponseCelsiusMeterPerKwMinute:
        bottomEnd.heatingResponseCelsiusMeterPerKwMinute,
    })),
  );
}

function toNormalizedBottomEnd(bottomEnd: BottomEnd): NormalizedBottomEnd {
  const coilSectionMm = roundTo(
    bottomEnd.bottomEndWidthMm -
      bottomEnd.firstContactEdgeOffsetMm -
      bottomEnd.lastContactEdgeOffsetMm,
    1,
  );
  const averageContactCenterSpacingMm = roundTo(
    bottomEnd.contactCenterSpacingsMm.reduce(
      (sum, spacingMm) => sum + spacingMm,
      0,
    ) / bottomEnd.contactCenterSpacingsMm.length,
    1,
  );

  return {
    ...bottomEnd,
    coilSectionMm,
    averageContactCenterSpacingMm,
    generatedTurnSpacingMm: averageContactCenterSpacingMm,
  };
}

function toLegacyCatalogueMatchedCoilConfiguration(
  coilConfiguration: CoilConfiguration,
): CatalogueMatchedCoilConfiguration {
  return {
    ...coilConfiguration,
    effectiveCoilInsideDiameterMm: coilConfiguration.coilFrameSizeMm,
    heatingLengthMm: coilConfiguration.coilCoverageMm,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function hasNonEmptyText(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeFiniteNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function classifyRecommendation(
  advisoryCauses: RecommendationAdvisoryCause[],
): Pick<RecommendedSetup, "recommendationStatus" | "recommendationReason"> {
  if (advisoryCauses.length > 0) {
    return {
      recommendationStatus: "marginal",
      recommendationReason:
        "The best available catalogue-matched setup was calculated, with advisory causes that require review before use.",
    };
  }

  return {
    recommendationStatus: "recommended",
    recommendationReason:
      "The selected power source and standard coil configuration fit the field joint, provide the required coil coverage, and are within the power source operating limits.",
  };
}

function buildAdvisoryCauses(
  powerUtilization: number,
  operatingElectricalValues: OperatingElectricalValues,
  operatingLimits: PowerSourceOperatingLimits,
  selectedCoilConfiguration: CatalogueMatchedCoilConfiguration,
  calculations: Omit<RecommendationCalculations, "heatingLengthMm">,
  advisoryThresholds: AdvisoryThresholds,
): RecommendationAdvisoryCause[] {
  const causes: RecommendationAdvisoryCause[] = [];
  const coilFrameSizeMarginMm =
    selectedCoilConfiguration.effectiveCoilInsideDiameterMm -
    calculations.requiredCoilInsideDiameterMm;
  const coilCoverageMarginMm =
    selectedCoilConfiguration.coilCoverageMm -
    calculations.requiredCoilCoverageMm;

  if (coilFrameSizeMarginMm < 0) {
    causes.push({
      code: "coil-frame-size-below-required",
      message:
        "The best catalogue coil frame is smaller than the required coil inside diameter.",
      measuredValue: selectedCoilConfiguration.effectiveCoilInsideDiameterMm,
      thresholdValue: calculations.requiredCoilInsideDiameterMm,
      deficitValue: Math.abs(roundTo(coilFrameSizeMarginMm, 1)),
    });
  } else if (
    coilFrameSizeMarginMm <=
    advisoryThresholds.minimumCoilFrameSizeMarginMm
  ) {
    causes.push({
      code: "coil-frame-size-close-to-required",
      message:
        "The coil frame size is close to the required coil inside diameter.",
      assumptionName: "advisoryThresholds.minimumCoilFrameSizeMarginMm",
      measuredValue: roundTo(coilFrameSizeMarginMm, 1),
      thresholdValue: advisoryThresholds.minimumCoilFrameSizeMarginMm,
    });
  }

  if (coilCoverageMarginMm < 0) {
    causes.push({
      code: "coil-coverage-below-required",
      message:
        "The best catalogue coil coverage is shorter than the required coil coverage.",
      measuredValue: selectedCoilConfiguration.coilCoverageMm,
      thresholdValue: calculations.requiredCoilCoverageMm,
      deficitValue: Math.abs(roundTo(coilCoverageMarginMm, 1)),
    });
  } else if (
    coilCoverageMarginMm <= advisoryThresholds.minimumCoilCoverageMarginMm
  ) {
    causes.push({
      code: "coil-coverage-close-to-required",
      message: "The coil coverage is close to the required coil coverage.",
      assumptionName: "advisoryThresholds.minimumCoilCoverageMarginMm",
      measuredValue: roundTo(coilCoverageMarginMm, 1),
      thresholdValue: advisoryThresholds.minimumCoilCoverageMarginMm,
    });
  }

  const lowPowerUtilizationThreshold = Math.max(
    advisoryThresholds.lowPowerUtilization,
    operatingLimits.minimumPowerUtilization,
  );

  if (powerUtilization <= lowPowerUtilizationThreshold) {
    causes.push({
      code: "low-power-utilization",
      message: "The calculated power utilization is low.",
      assumptionName:
        lowPowerUtilizationThreshold === advisoryThresholds.lowPowerUtilization
          ? "advisoryThresholds.lowPowerUtilization"
          : "selectedPowerSource.operatingLimits.minimumPowerUtilization",
      measuredValue: powerUtilization,
      thresholdValue: lowPowerUtilizationThreshold,
    });
  }

  if (operatingElectricalValues.outputLimited) {
    causes.push({
      code: "output-limited",
      message:
        "The final operating point is limited by the selected power source output limits.",
      outputLimitingReasons: [
        ...operatingElectricalValues.outputLimitingReasons,
      ],
    });
  }

  return causes;
}

function buildAdvisoryActions(
  advisoryCauses: RecommendationAdvisoryCause[],
): RecommendationAdvisoryAction[] {
  const causeCodes = advisoryCauses.map((cause) => cause.code);
  const actions: RecommendationAdvisoryAction[] = [];
  const customCoilCauseCodes = causeCodes.filter(
    (code) =>
      code === "coil-frame-size-below-required" ||
      code === "coil-coverage-below-required",
  );

  if (customCoilCauseCodes.length > 0) {
    actions.push({
      code: "custom-coil-design-review",
      message:
        "Review whether a custom coil design is required before using this catalogue match.",
      causeCodes: customCoilCauseCodes,
    });
  }

  if (causeCodes.includes("output-limited")) {
    actions.push({
      code: "review-output-limiting",
      message:
        "Review the output limiting condition against the required heating cycle.",
      causeCodes: ["output-limited"],
    });
  }

  const engineeringReviewCauseCodes = causeCodes.filter(
    (code) =>
      code !== "coil-frame-size-below-required" &&
      code !== "coil-coverage-below-required" &&
      code !== "output-limited",
  );

  if (engineeringReviewCauseCodes.length > 0) {
    actions.push({
      code: "engineering-review",
      message:
        "Engineering review should confirm the marginal catalogue match before use.",
      causeCodes: engineeringReviewCauseCodes,
    });
  }

  return actions;
}

function calculateRecommendationInputs(
  fieldJointGeometry: FieldJointGeometryInput,
  coilClearanceMm: number,
  requiredOverhangMm: number,
): Omit<RecommendationCalculations, "heatingLengthMm"> {
  const factoryCoatingOutsideDiameterMm =
    fieldJointGeometry.pipeOutsideDiameterMm +
    2 * fieldJointGeometry.factoryCoatingThicknessMm;
  const bareSteelLengthMm =
    fieldJointGeometry.sideACutbackMm + fieldJointGeometry.sideBCutbackMm;

  return {
    factoryCoatingOutsideDiameterMm,
    bareSteelLengthMm,
    requiredCoilInsideDiameterMm:
      factoryCoatingOutsideDiameterMm + 2 * coilClearanceMm,
    requiredCoilCoverageMm: bareSteelLengthMm + 2 * requiredOverhangMm,
  };
}

function calculateOptimumCoilRecommendation(
  requiredCoilCoverageMm: number,
  recommendationOptions: RecommendationOptions,
): OptimumCoilRecommendation {
  const preferredTurnSpacingMm = recommendationOptions.preferredTurnSpacingMm;
  const unconstrainedTurns = Math.ceil(
    requiredCoilCoverageMm / preferredTurnSpacingMm,
  );
  const numberOfTurns = clamp(
    unconstrainedTurns,
    recommendationOptions.turnBounds.minimumTurns,
    recommendationOptions.turnBounds.maximumTurns,
  );

  return {
    numberOfTurns,
    turnSpacingMm: preferredTurnSpacingMm,
    coilCoverageMm: numberOfTurns * preferredTurnSpacingMm,
  };
}

function buildCoilRecommendationComparison(
  optimumCoilRecommendation: OptimumCoilRecommendation,
  selectedCoilConfiguration: CatalogueMatchedCoilConfiguration | null,
): CoilRecommendationComparison {
  return {
    optimum: {
      ...optimumCoilRecommendation,
    },
    catalogueMatch:
      selectedCoilConfiguration === null
        ? null
        : {
            numberOfTurns: selectedCoilConfiguration.numberOfTurns,
            turnSpacingMm: selectedCoilConfiguration.turnSpacingMm,
            coilCoverageMm: selectedCoilConfiguration.coilCoverageMm,
            coilSectionMm:
              selectedCoilConfiguration.selectedBottomEnd?.coilSectionMm ?? null,
            heatingLengthMm: selectedCoilConfiguration.heatingLengthMm,
          },
  };
}

function selectPowerSource(
  powerSources: PowerSource[],
  selectedPowerSourceId: string | undefined,
  availablePowerSourceIds: string[] | undefined,
): PowerSource | undefined {
  const availablePowerSources =
    availablePowerSourceIds === undefined
      ? powerSources
      : powerSources.filter((powerSource) =>
          availablePowerSourceIds.includes(powerSource.id),
        );

  if (selectedPowerSourceId !== undefined) {
    return availablePowerSources.find(
      (powerSource) => powerSource.id === selectedPowerSourceId,
    );
  }

  return [...availablePowerSources].sort(
    (left, right) => left.powerSourceRatingKva - right.powerSourceRatingKva,
  )[0];
}

function selectBestCatalogueMatchedCoilConfiguration(
  coilConfigurations: CatalogueMatchedCoilConfiguration[],
  requiredCoilInsideDiameterMm: number,
  requiredCoilCoverageMm: number,
  powerSource: PowerSource,
  fieldJointGeometry: FieldJointGeometryInput,
  optimumCoilRecommendation: OptimumCoilRecommendation,
  recommendationOptions: RecommendationOptions,
): CatalogueMatchedCoilConfiguration {
  return [...coilConfigurations]
    .sort(
      (left, right) =>
        compareCatalogueMatchedCoilConfigurations(
          left,
          right,
          requiredCoilInsideDiameterMm,
          requiredCoilCoverageMm,
          optimumCoilRecommendation.numberOfTurns,
          powerSource,
          fieldJointGeometry,
          recommendationOptions,
        ),
    )[0] as CatalogueMatchedCoilConfiguration;
}

function compareCatalogueMatchedCoilConfigurations(
  left: CatalogueMatchedCoilConfiguration,
  right: CatalogueMatchedCoilConfiguration,
  requiredCoilInsideDiameterMm: number,
  requiredCoilCoverageMm: number,
  optimumNumberOfTurns: number,
  powerSource: PowerSource,
  fieldJointGeometry: FieldJointGeometryInput,
  recommendationOptions: RecommendationOptions,
): number {
  const leftFitPenalty = calculateCatalogueFitPenalty(
    left,
    requiredCoilInsideDiameterMm,
    requiredCoilCoverageMm,
  );
  const rightFitPenalty = calculateCatalogueFitPenalty(
    right,
    requiredCoilInsideDiameterMm,
    requiredCoilCoverageMm,
  );

  if (leftFitPenalty !== rightFitPenalty) {
    return leftFitPenalty - rightFitPenalty;
  }

  const leftTurnDistance = Math.abs(left.numberOfTurns - optimumNumberOfTurns);
  const rightTurnDistance = Math.abs(right.numberOfTurns - optimumNumberOfTurns);

  if (leftTurnDistance !== rightTurnDistance) {
    return leftTurnDistance - rightTurnDistance;
  }

  if (left.numberOfTurns !== right.numberOfTurns) {
    const higherTurnCandidate =
      left.numberOfTurns > right.numberOfTurns ? left : right;
    const lowerTurnCandidate =
      higherTurnCandidate === left ? right : left;

    if (
      calculatePowerMatchScore(
        higherTurnCandidate,
        powerSource,
        fieldJointGeometry,
        recommendationOptions,
      ) <=
      calculatePowerMatchScore(
        lowerTurnCandidate,
        powerSource,
        fieldJointGeometry,
        recommendationOptions,
      )
    ) {
      return higherTurnCandidate === left ? -1 : 1;
    }

    return lowerTurnCandidate === left ? -1 : 1;
  }

  return (
    left.effectiveCoilInsideDiameterMm - right.effectiveCoilInsideDiameterMm ||
    left.coilCoverageMm - right.coilCoverageMm
  );
}

function calculateCatalogueFitPenalty(
  coilConfiguration: CatalogueMatchedCoilConfiguration,
  requiredCoilInsideDiameterMm: number,
  requiredCoilCoverageMm: number,
): number {
  const coilFrameDeficitMm = Math.max(
    requiredCoilInsideDiameterMm -
      coilConfiguration.effectiveCoilInsideDiameterMm,
    0,
  );
  const coilCoverageDeficitMm = Math.max(
    requiredCoilCoverageMm - coilConfiguration.coilCoverageMm,
    0,
  );

  return coilFrameDeficitMm + coilCoverageDeficitMm;
}

function calculatePowerMatchScore(
  coilConfiguration: CatalogueMatchedCoilConfiguration,
  powerSource: PowerSource,
  fieldJointGeometry: FieldJointGeometryInput,
  recommendationOptions: RecommendationOptions,
): number {
  const powerUtilization = calculateOperatingPoint(
    coilConfiguration,
    powerSource,
    fieldJointGeometry,
    recommendationOptions,
  ).powerUtilization;
  const targetPowerUtilization =
    (powerSource.operatingLimits.minimumPowerUtilization +
      powerSource.operatingLimits.maximumPowerUtilization) /
    2;

  return Math.abs(powerUtilization - targetPowerUtilization);
}

function calculateOperatingPoint(
  coilConfiguration: CatalogueMatchedCoilConfiguration,
  powerSource: PowerSource,
  fieldJointGeometry: FieldJointGeometryInput,
  recommendationOptions: RecommendationOptions,
): OperatingPoint {
  const estimatedPowerUtilization = calculateEstimatedPowerUtilization(
    coilConfiguration,
    powerSource,
    fieldJointGeometry,
    recommendationOptions,
  );
  const powerFactor = calculatePowerFactor(
    estimatedPowerUtilization,
    coilConfiguration,
    powerSource,
    recommendationOptions,
  );
  const ratedApparentPowerKva =
    (Math.sqrt(3) *
      powerSource.maximumPowerSourceVoltageV *
      powerSource.maximumPowerSourceCurrentA) /
    1_000;
  const requestedDeliveredPowerKw =
    powerSource.powerSourceRatingKva * estimatedPowerUtilization;
  const requestedApparentPowerKva = requestedDeliveredPowerKw / powerFactor;
  const apparentPowerRatio =
    ratedApparentPowerKva <= 0
      ? 0
      : requestedApparentPowerKva / ratedApparentPowerKva;
  const demandMagnitude = Math.sqrt(Math.max(apparentPowerRatio, 0));
  const voltageDemandFactor = calculateVoltageDemandFactor(coilConfiguration);
  const requestedPowerSourceVoltageV =
    powerSource.maximumPowerSourceVoltageV *
    demandMagnitude *
    voltageDemandFactor;
  const requestedPowerSourceCurrentA =
    powerSource.maximumPowerSourceCurrentA *
    demandMagnitude /
    voltageDemandFactor;
  const powerSourceVoltageV = Math.min(
    requestedPowerSourceVoltageV,
    powerSource.maximumPowerSourceVoltageV,
  );
  const powerSourceCurrentA = Math.min(
    requestedPowerSourceCurrentA,
    powerSource.maximumPowerSourceCurrentA,
  );
  const outputLimitingReasons: PowerSourceOutputLimitReason[] = [
    ...(requestedPowerSourceVoltageV > powerSource.maximumPowerSourceVoltageV
      ? (["voltage"] as const)
      : []),
    ...(requestedPowerSourceCurrentA > powerSource.maximumPowerSourceCurrentA
      ? (["current"] as const)
      : []),
  ];
  const deliveredPowerKw = roundTo(
    (Math.sqrt(3) * powerSourceVoltageV * powerSourceCurrentA * powerFactor) /
      1_000,
    1,
  );
  const powerUtilization = roundTo(
    deliveredPowerKw / powerSource.powerSourceRatingKva,
    2,
  );

  return {
    operatingElectricalValues: {
      powerSourceVoltageV: roundTo(powerSourceVoltageV, 1),
      powerSourceCurrentA: roundTo(powerSourceCurrentA, 1),
      powerFactor,
      outputLimited: outputLimitingReasons.length > 0,
      outputLimitingReasons,
    },
    deliveredPowerKw,
    powerUtilization,
    estimatedPowerUtilization,
  };
}

function calculatePowerFactor(
  estimatedPowerUtilization: number,
  coilConfiguration: CatalogueMatchedCoilConfiguration,
  powerSource: PowerSource,
  recommendationOptions: RecommendationOptions,
): number {
  return clamp(
    roundTo(
      0.82 +
        0.1 * estimatedPowerUtilization -
        0.02 *
          Math.abs(
            relativeDelta(
              powerSource.powerSourceFrequencyHz,
              recommendationOptions.referenceFrequencyHz,
            ),
          ) +
        0.02 *
          relativeDelta(coilConfiguration.numberOfTurns, referenceNumberOfTurns),
      2,
    ),
    0.7,
    0.95,
  );
}

function calculateVoltageDemandFactor(
  coilConfiguration: CatalogueMatchedCoilConfiguration,
): number {
  return clamp(
    Math.sqrt(
      (coilConfiguration.effectiveCoilInsideDiameterMm /
        referenceEffectiveCoilInsideDiameterMm) *
        (coilConfiguration.turnSpacingMm / referenceTurnSpacingMm) *
        (referenceNumberOfTurns / coilConfiguration.numberOfTurns),
    ),
    0.5,
    2,
  );
}

function calculateEstimatedPowerUtilization(
  coilConfiguration: CatalogueMatchedCoilConfiguration,
  powerSource: PowerSource,
  fieldJointGeometry: FieldJointGeometryInput,
  recommendationOptions: RecommendationOptions,
): number {
  const coefficients = recommendationOptions.modelCoefficients;
  const bottomEndWidthMm =
    coilConfiguration.selectedBottomEnd?.bottomEndWidthMm ??
    coilConfiguration.coilCoverageMm;

  return clamp(
    roundTo(
      coefficients.powerUtilizationBase +
        coefficients.powerUtilizationFrequencyCoefficient *
          relativeDelta(
            powerSource.powerSourceFrequencyHz,
            recommendationOptions.referenceFrequencyHz,
          ) +
        coefficients.powerUtilizationVoltageCoefficient *
          relativeDelta(
            powerSource.maximumPowerSourceVoltageV,
            referencePowerSourceVoltageV,
          ) +
        coefficients.powerUtilizationCurrentCoefficient *
          relativeDelta(
            powerSource.maximumPowerSourceCurrentA,
            referencePowerSourceCurrentA,
          ) +
        coefficients.powerUtilizationPipeOutsideDiameterCoefficient *
          relativeDelta(
            fieldJointGeometry.pipeOutsideDiameterMm,
            referenceHeatingPipeOutsideDiameterMm,
          ) +
        coefficients.powerUtilizationPipeWallThicknessCoefficient *
          relativeDelta(
            fieldJointGeometry.pipeWallThicknessMm,
            referenceHeatingPipeWallThicknessMm,
          ) +
        coefficients.powerUtilizationFrameDiameterCoefficient *
          relativeDelta(
            coilConfiguration.effectiveCoilInsideDiameterMm,
            referenceEffectiveCoilInsideDiameterMm,
          ) +
        coefficients.powerUtilizationTurnsCoefficient *
          relativeDelta(coilConfiguration.numberOfTurns, referenceNumberOfTurns) +
        coefficients.powerUtilizationBottomEndWidthCoefficient *
          relativeDelta(bottomEndWidthMm, referenceBottomEndWidthMm) +
        coefficients.powerUtilizationTurnSpacingCoefficient *
          relativeDelta(coilConfiguration.turnSpacingMm, referenceTurnSpacingMm),
      2,
    ),
    0,
    1,
  );
}

function evaluateStandardCoilCatalogue(
  coilConfigurations: CatalogueMatchedCoilConfiguration[],
  requiredCoilInsideDiameterMm: number,
  requiredCoilCoverageMm: number,
): CatalogueEvaluation {
  const fittingCoilConfigurationCount = coilConfigurations.filter(
    (coilConfiguration) =>
      coilConfiguration.effectiveCoilInsideDiameterMm >=
        requiredCoilInsideDiameterMm &&
      coilConfiguration.coilCoverageMm >= requiredCoilCoverageMm,
  ).length;

  return {
    standardCoilConfigurationCount: coilConfigurations.length,
    fittingCoilConfigurationCount,
    hasCoilFrameSizeFit: coilConfigurations.some(
      (coilConfiguration) =>
        coilConfiguration.effectiveCoilInsideDiameterMm >=
          requiredCoilInsideDiameterMm,
    ),
    hasCoilCoverageFit: coilConfigurations.some(
      (coilConfiguration) =>
        coilConfiguration.coilCoverageMm >= requiredCoilCoverageMm,
    ),
  };
}

function estimateHeatingCycleTimeMinutes(
  fieldJointGeometry: FieldJointGeometryInput,
  heating: HeatingInput,
  heatingLengthMm: number,
  deliveredPowerKw: number,
  heatingResponseCelsiusMeterPerKwMinute: number,
  powerSource: PowerSource,
  recommendationOptions: RecommendationOptions,
): number | null {
  const startingTemperatureC = resolveStartingTemperatureC(heating);
  const temperatureRiseC = heating.targetHeatTemperatureC - startingTemperatureC;

  if (temperatureRiseC <= 0) {
    return 0;
  }

  if (deliveredPowerKw <= 0 || heatingResponseCelsiusMeterPerKwMinute <= 0) {
    return null;
  }

  const fieldJointGeometryFactor =
    (fieldJointGeometry.pipeOutsideDiameterMm *
      fieldJointGeometry.pipeWallThicknessMm) /
    (referenceHeatingPipeOutsideDiameterMm *
      referenceHeatingPipeWallThicknessMm);
  const heatingLengthM = heatingLengthMm / 1_000;
  const frequencyFactor = calculateFrequencyFactor(
    powerSource.powerSourceFrequencyHz,
    recommendationOptions.frequencyCalibrationCurve,
    "heatingCycleFactor",
  );

  return roundTo(
    ((temperatureRiseC * heatingLengthM * fieldJointGeometryFactor) /
      (deliveredPowerKw * heatingResponseCelsiusMeterPerKwMinute)) *
      frequencyFactor,
    1,
  );
}

function resolveStartingTemperatureC(heating: HeatingInput): number {
  return heating.startingTemperatureC ?? heating.ambientTemperatureC ?? 20;
}

function resolveStartingTemperatureSource(
  heating: HeatingInput,
): StartingTemperatureSource {
  if (heating.startingTemperatureC !== undefined) {
    return "starting temperature input";
  }

  if (heating.ambientTemperatureC !== undefined) {
    return "ambient temperature input";
  }

  return "default ambient temperature";
}

function estimateCooldownTimeMinutes(
  fieldJointGeometry: FieldJointGeometryInput,
  heating: HeatingInput,
  applicationProduct: ApplicationProductInput | undefined,
  powerSource: PowerSource,
  recommendationOptions: RecommendationOptions,
  heatingCycleTimeMinutes: number | null,
): number | undefined {
  const subsequentProcessTemperatureC =
    applicationProduct?.subsequentProcessTemperatureC;

  if (subsequentProcessTemperatureC === undefined) {
    return undefined;
  }

  const cooldownTemperatureDropC =
    heating.targetHeatTemperatureC - subsequentProcessTemperatureC;

  if (cooldownTemperatureDropC <= 0) {
    return 0;
  }

  if (heatingCycleTimeMinutes === null) {
    return undefined;
  }

  const frequencyFactor = calculateFrequencyFactor(
    powerSource.powerSourceFrequencyHz,
    recommendationOptions.frequencyCalibrationCurve,
    "cooldownFactor",
  );
  const heatSoakFactor = calculateHeatSoakFactor(
    heatingCycleTimeMinutes,
    fieldJointGeometry.pipeWallThicknessMm,
    recommendationOptions,
  );

  return roundTo(
    (cooldownTemperatureDropC / baseCooldownRateCelsiusPerMinute) *
      frequencyFactor *
      heatSoakFactor,
    1,
  );
}

function calculateFrequencyFactor(
  selectedFrequencyHz: number,
  frequencyCalibrationCurve: FrequencyCalibrationPoint[],
  factorKey: "heatingCycleFactor" | "cooldownFactor",
): number {
  const sortedCurve = [...frequencyCalibrationCurve].sort(
    (left, right) => left.frequencyHz - right.frequencyHz,
  );
  const firstPoint = sortedCurve[0];
  const lastPoint = sortedCurve[sortedCurve.length - 1];

  if (firstPoint === undefined || lastPoint === undefined) {
    return 1;
  }

  if (selectedFrequencyHz <= firstPoint.frequencyHz) {
    return roundTo(clamp(firstPoint[factorKey], 0.5, 1.5), 3);
  }

  if (selectedFrequencyHz >= lastPoint.frequencyHz) {
    return roundTo(clamp(lastPoint[factorKey], 0.5, 1.5), 3);
  }

  for (let index = 0; index < sortedCurve.length - 1; index += 1) {
    const leftPoint = sortedCurve[index];
    const rightPoint = sortedCurve[index + 1];

    if (
      leftPoint === undefined ||
      rightPoint === undefined ||
      selectedFrequencyHz < leftPoint.frequencyHz ||
      selectedFrequencyHz > rightPoint.frequencyHz
    ) {
      continue;
    }

    const frequencyRangeHz = rightPoint.frequencyHz - leftPoint.frequencyHz;
    const frequencyPosition =
      (selectedFrequencyHz - leftPoint.frequencyHz) / frequencyRangeHz;
    const interpolatedFactor =
      leftPoint[factorKey] +
      (rightPoint[factorKey] - leftPoint[factorKey]) * frequencyPosition;

    return roundTo(clamp(interpolatedFactor, 0.5, 1.5), 3);
  }

  return 1;
}

function calculateHeatSoakFactor(
  heatingCycleTimeMinutes: number,
  pipeWallThicknessMm: number,
  recommendationOptions: RecommendationOptions,
): number {
  const coefficients = recommendationOptions.modelCoefficients;

  return roundTo(
    clamp(
      1 +
        coefficients.cooldownHeatingCycleHeatSoakCoefficient *
          relativeDelta(
            heatingCycleTimeMinutes,
            referenceHeatSoakHeatingCycleTimeMinutes,
          ) +
        coefficients.cooldownPipeWallThicknessCoefficient *
          relativeDelta(pipeWallThicknessMm, referenceHeatingPipeWallThicknessMm),
      0.5,
      2,
    ),
    3,
  );
}

function roundTo(value: number, decimals: number): number {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function relativeDelta(value: number, referenceValue: number): number {
  return (value - referenceValue) / referenceValue;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}
